import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Label, Text, useId, TabList, Tab } from '@fluentui/react-components';
import {
  LoadingIndicator, PromptEditor, MAX_PROMPT_LENGTH, PromptEditorV2,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { ExampleImageList, ExampleImageCell } from '../example-image';
import { GenerationHistoryList, GenerationHistoryListV2 } from '../history-list';
import { GenerateButton } from '../generate-button';
import { promptViewTabs } from '../prompt-control-state';

const INITIAL_CONTROL_HEIGHT = 320;
const CONTAINER_OTHER_HEIGHT = 140; // padding + tab + title

export const PromptControlV2 = (props) => {
  const {
    i18n,
    generate,
    prompt,
    setPrompt,
    isGenerating,
    onSelectExampleImage,
    MultiLineEllipsisText,
    historyList,
    onSelectHistory,
    selectedHistoryGroup,
    disabled,
    onChangePromptView,
    selectedTab,
  } = props;

  const [promptControlHeight, setPromptControlHeight] = useState(INITIAL_CONTROL_HEIGHT);
  const exampleImageId = useId('example-image-list');
  const textAreaRef = useRef();

  const onSelectExample = useCallback((promptText, index) => {
    if (textAreaRef.current && textAreaRef.current.focus) {
      textAreaRef.current.focus();
    }
    onSelectExampleImage(promptText, index);
  }, [onSelectExampleImage]);

  const renderExampleImageList = () => (
    <ExampleImageList
      i18n={i18n}
      labelId={exampleImageId}
      MultiLineEllipsisText={MultiLineEllipsisText}
      ExampleImageCell={ExampleImageCell}
      onSelectImage={onSelectExample}
    />
  );

  useEffect(() => {
    const containerHeight = document.querySelector('.control-section').clientHeight;
    setPromptControlHeight(containerHeight - CONTAINER_OTHER_HEIGHT);
  }, []);

  return (
    <div className="prompt-control" style={{ height: promptControlHeight, overflowY: 'auto' }}>
      <div className="background-change-prompt">
        <PromptEditorV2
          textAreaRef={textAreaRef}
          i18n={i18n}
          prompt={prompt}
          setPrompt={setPrompt}
          disabledText={isGenerating || disabled}
          placeholder={i18n.getString(_TL_('Describe the background you want. You can use the examples on the below for inspiration.'))}
        />
      </div>
      {isGenerating &&
        <div className="loading-section">
          <LoadingIndicator />
          <Text size={200} className="loading-text">{i18n.getString(_TL_('Creating background images with AI'))}</Text>
        </div>}
      {!isGenerating && !(historyList && historyList.length > 0) && (
        <div className="prompt-example prompt-example-new">
          <Label size="small" weight="semibold" className="example-image-list-label" id={exampleImageId}>
            {i18n.getString(_TL_('Try an example'))}
          </Label>
          {renderExampleImageList()}
        </div>
      )}
      <GenerateButton i18n={i18n} disabledButton={isGenerating || !prompt || prompt.length === 0 || prompt.length > MAX_PROMPT_LENGTH || disabled} onClickGenerateButton={generate} />
      {!isGenerating && historyList && historyList.length > 0 && (
        <>
          <div className="prompt-view-tabs">
            <TabList selectedValue={selectedTab} onTabSelect={onChangePromptView}>
              <Tab value={promptViewTabs.Example} >
                {i18n.getString(_TL_('Explore ideas'))}
              </Tab>
              <Tab value={promptViewTabs.History} >
                {i18n.getString(_TL_('My creations'))}
              </Tab>
            </TabList>
          </div>
          {selectedTab === promptViewTabs.History && (
            <GenerationHistoryListV2
              selectedHistoryGroup={selectedHistoryGroup}
              i18n={i18n}
              onSelectHistory={onSelectHistory}
              historyList={historyList}
            />)}
          {selectedTab === promptViewTabs.Example && (renderExampleImageList())}
        </>
      )}
    </div>
  );
};

export const PromptControl = (props) => {
  const {
    i18n,
    generate,
    prompt,
    setPrompt,
    isGenerating,
    onSelectExampleImage,
    MultiLineEllipsisText,
    historyList,
    onSelectHistory,
    selectedHistoryIndex,
    disabled,
  } = props;

  const exampleImageId = useId('example-image-list');

  return (
    <div className="prompt-control">
      <div className="background-change-prompt">
        <PromptEditor
          i18n={i18n}
          prompt={prompt}
          setPrompt={setPrompt}
          disabledText={disabled}
          onClickGenerateButton={generate}
          disabledButton={isGenerating || !prompt || prompt.length === 0 || prompt.length > MAX_PROMPT_LENGTH || disabled}
          placeholder={i18n.getString(_TL_('Describe the background you want. You can use the examples on the below for inspiration.'))}
        />
      </div>
      {isGenerating &&
        <div className="loading-section">
          <LoadingIndicator />
          <Text size={200} className="loading-text">{i18n.getString(_TL_('Creating background images with AI'))}</Text>
        </div>}
      {!isGenerating && (
        <div className="prompt-example">
          <Label size="small" weight="semibold" className="example-image-list-label" id={exampleImageId}>
            {i18n.getString(_TL_('Try an example'))}
          </Label>
          <ExampleImageList
            i18n={i18n}
            labelId={exampleImageId}
            MultiLineEllipsisText={MultiLineEllipsisText}
            ExampleImageCell={ExampleImageCell}
            onSelectImage={onSelectExampleImage}
          />
        </div>)}
      {!isGenerating && historyList && historyList.length > 0 && (
        <GenerationHistoryList
          selectedIndex={selectedHistoryIndex}
          i18n={i18n}
          onSelectHistory={onSelectHistory}
          historyList={historyList}
        />)}
    </div>
  );
};
